<template>
    <div>
        <form-wrapper class="form__fields" :isSubmittable="false">
            <div class="auth-token-form">
                <div v-if="showNpiField" class="auth-token-form__field">
                    <text-field
                        :label="$t('pages.auth.verify_token.form.npi_number.label')"
                        :placeholder="$t('pages.auth.verify_token.form.npi_number.placeholder')"
                        :isRequired="requiresNpi"
                        :hasAutofocus="true"
                        :formData="data"
                        :isDisabled="isLoading"
                        :error="npiErrorMessage"
                        :maxLength="10"
                        valuePath="npiNumber"
                        @change="change($event)"
                    ></text-field>
                    <p class="auth-token-form__help">
                        {{ $t('pages.auth.verify_token.form.npi_number.help') }}
                    </p>
                </div>

                <div v-if="showTokenField" class="auth-token-form__field">
                    <text-field
                        :label="$t('pages.auth.verify_token.form.token.label')"
                        :placeholder="$t('pages.auth.verify_token.form.token.placeholder')"
                        :isRequired="true"
                        :hasAutofocus="!showNpiField"
                        :formData="data"
                        :isDisabled="isLoading || isProbing"
                        :error="tokenErrorMessage"
                        valuePath="token"
                        @change="change($event)"
                    ></text-field>
                    <p class="auth-token-form__help">
                        {{ $t('pages.auth.verify_token.form.token.help') }}
                    </p>
                </div>

                <div v-if="showEmailField" class="auth-token-form__field">
                    <text-field
                        :label="$t('pages.auth.verify_email.form.email.label')"
                        :placeholder="$t('pages.auth.verify_email.form.email.placeholder')"
                        :isRequired="true"
                        :hasAutofocus="true"
                        :formData="data"
                        :isDisabled="isLoading"
                        :error="emailErrorMessage"
                        valuePath="email"
                        type="email"
                        @change="change($event)"
                    ></text-field>
                </div>

                <div v-if="showVerifyButton" class="verify-buttons">
                    <common-button
                        :label="verifyButtonLabel"
                        :isProcessing="isLoading || isProbing"
                        type="primary"
                        size="large"
                        :isFluid="true"
                        @click="verifyToken"
                    ></common-button>
                </div>
            </div>
        </form-wrapper>

        <verify-email-modal
            :isOpen="isEmailModalOpen"
            :npiNumber="data.npiNumber || null"
            :token="data.token"
            :closeOnClickAway="false"
            @close="isEmailModalOpen = false"
            @email-submitted="handleEmailSubmitted"
        ></verify-email-modal>

        <verify-code-modal
            :isOpen="isCodeModalOpen"
            :email="userEmail"
            :accessLogId="externalProviderReferralsAccessLogId"
            :closeOnClickAway="false"
            @close="isCodeModalOpen = false"
            @code-verified="handleCodeVerified"
        ></verify-code-modal>

        <referral-info-modal
            :isOpen="isReferralInfoModalOpen"
            :referral="referral"
            :referralPoolId="referral && referral.id ? String(referral.id) : null"
            :npiNumber="data.npiNumber || null"
            :token="data.token"
            :closeOnClickAway="false"
            @close="isReferralInfoModalOpen = false"
        ></referral-info-modal>
    </div>
</template>

<script>
    import FormMixin from 'mixins/FormMixin'
    import VerifyEmailModal from 'components/page-elements/auth/VerifyEmailModal'
    import VerifyCodeModal from 'components/page-elements/auth/VerifyCodeModal'
    import ReferralInfoModal from 'components/page-elements/auth/ReferralInfoModal'
    import ReferralsPoolAPI from 'api/ReferralsPoolAPI'

    export default {
        mixins: [FormMixin],

        components: {
            VerifyEmailModal,
            VerifyCodeModal,
            ReferralInfoModal,
        },

        data() {
            return {
                isLoading: false,
                isProbing: false,
                npiErrorMessage: '',
                tokenErrorMessage: '',
                emailErrorMessage: '',
                data: {
                    npiNumber: '',
                    token: '',
                    email: '',
                },
                requiresNpi: false,
                isEmailModalOpen: false,
                isCodeModalOpen: false,
                isReferralInfoModalOpen: false,
                userEmail: '',
                externalProviderReferralsAccessLogId: null,
                referral: null,
            }
        },

        computed: {
            queryNpi() {
                return this.$route.query.npi || this.$route.query.npiNumber || ''
            },

            hasQueryToken() {
                return Boolean(this.$route.query.token)
            },

            hasQueryNpi() {
                return Boolean(this.queryNpi)
            },

            hasQueryTokenAndNpi() {
                return this.hasQueryToken && this.hasQueryNpi
            },

            showEmailField() {
                return this.hasQueryTokenAndNpi && !this.requiresNpi && !this.npiErrorMessage && !this.tokenErrorMessage
            },

            showNpiField() {
                if (this.hasQueryTokenAndNpi) {
                    return Boolean(this.npiErrorMessage) || this.requiresNpi
                }
                if (this.hasQueryToken) {
                    return this.requiresNpi
                }
                return true
            },

            showTokenField() {
                if (this.hasQueryTokenAndNpi) {
                    return Boolean(this.tokenErrorMessage)
                }
                if (this.hasQueryToken) {
                    return Boolean(this.tokenErrorMessage) && !this.requiresNpi
                }
                return true
            },

            showVerifyButton() {
                if (this.isProbing) return false
                return this.showNpiField || this.showTokenField || this.showEmailField
            },

            verifyButtonLabel() {
                if (this.showEmailField) {
                    return this.$t('pages.auth.verify_email.form.send_code')
                }
                return this.$t('pages.auth.verify_token.form.verify')
            },
        },

        methods: {
            emitPageStatus() {
                let status = 'manual'
                if (this.isProbing) status = 'verifying'
                else if (this.requiresNpi) status = 'needs_npi'
                else if (this.showEmailField) status = 'needs_email'
                else if (this.hasQueryToken) status = 'token_only'
                this.$emit('status-change', status)
            },

            handleAccessSuccess(response) {
                if (response.requiresNpi) {
                    this.requiresNpi = true
                    this.isLoading = false
                    this.isProbing = false
                    this.emitPageStatus()
                    return
                }

                if (response.emailRegistered || response.contactKnown) {
                    ReferralsPoolAPI.verifyWithToken(response.token, response.externalProviderReferralsAccessLogId)
                        .then((referral) => {
                            this.isReferralInfoModalOpen = true
                            this.referral = referral
                        })
                        .catch(() => {
                            this.tokenErrorMessage = this.$t('pages.auth.verify_token.errors.token_invalid')
                        })
                        .finally(() => {
                            this.isLoading = false
                            this.isProbing = false
                            this.emitPageStatus()
                        })
                    return
                }

                this.isLoading = false
                this.isProbing = false
                this.isEmailModalOpen = true
                this.emitPageStatus()
            },

            handleAccessError(error) {
                this.error = error.response
                this.isLoading = false
                this.isProbing = false

                const firstError = this.error && this.error.data && this.error.data[0]
                if (firstError && firstError.field === 'email') {
                    this.emailErrorMessage = this.$t('pages.auth.verify_email.errors.email_invalid')
                } else if (firstError && firstError.field === 'npiNumber') {
                    this.npiErrorMessage = this.$t('pages.auth.verify_token.errors.npi_mismatch')
                } else if (firstError && firstError.field === 'token' && firstError.code === 'entity.expired') {
                    this.tokenErrorMessage = this.$t('pages.auth.verify_token.errors.token_expired')
                } else if (firstError && firstError.field === 'token' && firstError.code === 'entity.invalid') {
                    this.tokenErrorMessage = this.$t('pages.auth.verify_token.errors.token_invalid')
                } else if (this.hasQueryToken) {
                    this.tokenErrorMessage = this.$t('pages.auth.verify_token.errors.token_invalid_token_only')
                } else {
                    this.tokenErrorMessage = this.$t('pages.auth.verify_token.errors.token_invalid_common')
                }
                this.emitPageStatus()
            },

            probeToken() {
                this.tokenErrorMessage = ''
                this.npiErrorMessage = ''
                this.isProbing = true
                this.emitPageStatus()

                ReferralsPoolAPI.accessWithToken(this.normalizedNpi() || undefined, this.data.token.trim())
                    .then((response) => this.handleAccessSuccess(response))
                    .catch((error) => this.handleAccessError(error))
            },

            normalizedNpi() {
                return (this.data.npiNumber || '').trim()
            },

            isValidEmail(email) {
                return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
            },

            submitEmailForReferral() {
                this.emailErrorMessage = ''

                const email = (this.data.email || '').trim()
                if (!email) {
                    this.emailErrorMessage = this.$t('pages.auth.verify_email.errors.email_required')
                    return
                }

                if (!this.isValidEmail(email)) {
                    this.emailErrorMessage = this.$t('pages.auth.verify_email.errors.email_invalid')
                    return
                }

                this.isLoading = true

                ReferralsPoolAPI.accessWithToken(this.normalizedNpi() || undefined, this.data.token.trim(), email)
                    .then((response) => {
                        this.isLoading = false
                        this.handleEmailSubmitted({
                            email,
                            externalProviderReferralsAccessLogId: response.externalProviderReferralsAccessLogId,
                        })
                    })
                    .catch((error) => this.handleAccessError(error))
            },

            verifyToken() {
                this.npiErrorMessage = ''
                this.tokenErrorMessage = ''
                this.emailErrorMessage = ''

                if (this.showEmailField) {
                    this.submitEmailForReferral()
                    return
                }

                const npiNumber = this.normalizedNpi()

                if (this.requiresNpi && !npiNumber) {
                    this.npiErrorMessage = this.$t('pages.auth.verify_token.errors.npi_required')
                    return
                }

                if (npiNumber && npiNumber.length !== 10) {
                    this.npiErrorMessage = this.$t('pages.auth.verify_token.errors.npi_must_be_10_digits')
                    return
                }

                if (!this.data.token || !this.data.token.trim()) {
                    this.tokenErrorMessage = this.$t('pages.auth.verify_token.errors.token_required')
                    return
                }

                this.isLoading = true

                ReferralsPoolAPI.accessWithToken(npiNumber || undefined, this.data.token.trim())
                    .then((response) => this.handleAccessSuccess(response))
                    .catch((error) => this.handleAccessError(error))
            },

            handleEmailSubmitted(payload) {
                this.userEmail = payload.email
                this.externalProviderReferralsAccessLogId = payload.externalProviderReferralsAccessLogId
                this.isEmailModalOpen = false
                this.isCodeModalOpen = true
            },

            handleCodeVerified(referral) {
                this.isCodeModalOpen = false
                this.isReferralInfoModalOpen = true
                this.referral = referral
            },
        },

        mounted() {
            if (this.$route.query.token) {
                this.data.token = this.$route.query.token
            }

            if (this.queryNpi) {
                this.data.npiNumber = String(this.queryNpi)
            }

            if (this.hasQueryTokenAndNpi) {
                if (this.normalizedNpi().length !== 10) {
                    this.npiErrorMessage = this.$t('pages.auth.verify_token.errors.npi_must_be_10_digits')
                }
                this.emitPageStatus()
                return
            }

            if (this.hasQueryToken) {
                this.probeToken()
                return
            }

            this.emitPageStatus()
        },
    }
</script>

<style scoped lang="scss">
    @import 'styles/setup/_variables.scss';
    @import 'styles/setup/_functions.scss';

    .auth-token-form {
        display: flex;
        flex-direction: column;
        gap: rem(20px);
        text-align: left;

        ::v-deep .form-field__label {
            color: $color--accent-3;
            font-weight: 600;
        }
    }

    .auth-token-form__field {
        display: flex;
        flex-direction: column;
        gap: rem(6px);
    }

    .auth-token-form__help {
        margin: 0;
        color: $color--txt-base-3;
        font-size: rem($font-size--hint);
        line-height: $line-height--copy;
    }

    .verify-buttons {
        margin-top: rem(4px);
        width: 100%;

        ::v-deep .button {
            width: 100%;
            background: $color--accent-3;
            text-transform: none;
            letter-spacing: normal;
            font-weight: 600;

            &:not([disabled]):hover,
            &:not([disabled]):focus {
                background: darken($color--accent-3, 6%);
            }
        }
    }
</style>
